import {
    BorderProps,
    ColorProps,
    FlexboxProps,
    GridProps,
    LayoutProps,
    SpaceProps,
    border,
    color,
    flexbox,
    grid,
    layout,
    space
} from "styled-system";
import { getWidthString, media } from "./style-utils-coras-nua";
import styled, { css } from "styled-components";

import defaultTheme from "./theme-test-override";
import { ThemeProps, ThemeTemplate } from "./theme-template";

type FlexboxTypes = SpaceProps &
    LayoutProps &
    BorderProps &
    ColorProps &
    FlexboxProps & { debug?: boolean } & { fullHeight?: boolean } & {
        fullHeightSmall?: boolean;
    } & { className?: string } & {
        fullWidth?: boolean;
    } &
    ThemeProps;
type ColumnTypes = SpaceProps &
    BorderProps &
    ColorProps &
    SpaceProps &
    FlexboxProps &
    LayoutProps & { hideSmall?: boolean } & { hideXsmall?: boolean } & {
        hideMedium?: boolean;
    } & { hideLarge?: boolean } & { hideXLarge?: boolean } & {
        xxs?: number;
    } & { xs?: number } & {
        sm?: number;
    } & { md?: number } & { lg?: number } & { xl?: number } & {
        position?: string;
    };
type GridTypes = LayoutProps &
    GridProps &
    ColorProps &
    SpaceProps & { gridTemplateColumns?: number } & {
        gridTemplateColumnsMobile?: number;
    } & { gridGap?: string } & { gridGapMobile?: string };

type MediaWidthProps = {
    xxs: number;
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    theme: {
        [key: string]: any;
    };
};

const Flexbox = styled.div<FlexboxTypes>`
    ${space}
    ${layout}
    ${color}
    ${flexbox}
    ${border}
    ${props =>
        props.debug &&
        css`
        > * {
            border: solid 2px ${props.theme.colors.brilliant};
            background: lightgrey;
            ${media.xs.up`
                background: lightpink;
            `}
            ${media.sm.up`
                background: lightsalmon;
            `}
            ${media.md.up`
                background: lightblue;
            `}
            ${media.lg.up`
                background: lightgoldenrodyellow;
            `}
            ${media.xl.up`
                background: lightseagreen;
            `}
        }
    `}
    ${props =>
        props.fullHeight &&
        css`
            flex: 1 0 auto;
        `}
    ${props =>
        props.fullHeightSmall &&
        css`
            ${media.sm.down`
                flex: 1 0 auto;
            `}
        `}
    ${props =>
        props.fullWidth &&
        css`
            flex-basis: 100%;
        `}
        // flex-basis : auto;
    ${props =>
        {
            if (props.className === "name-field") {
                const space = props.theme.space[2];
                return css`
                    > div:nth-child(odd) {
                        > div {
                            margin-right: ${space};
                            ${css`
                                ${media.sm.down`
                                    margin-right: 0
                                `}
                            `}
                        }
                    }
                    > div:nth-child(even) {
                        > div {
                            margin-left: ${space};
                            ${css`
                                ${media.sm.down`
                                    margin-left: 0
                                `}
                            `}
                        }
                    }
                `
            }
        }}
`;
Flexbox.defaultProps = {
    mx: "auto",
    maxWidth: "1280px",
    display: "flex",
    flexWrap: "wrap",
    theme: {
        ...defaultTheme
    } as ThemeTemplate
};

const Column = styled.div<ColumnTypes>`
    ${border}
    ${space}
    ${color}
    ${flexbox}
    ${layout}
    box-sizing: border-box;
    ${props =>
        props.position &&
        css`
            position: ${props.position};
        `}

    ${({ hideSmall, hideXsmall, hideMedium, hideLarge, hideXLarge }) => css`
        ${hideXsmall && media.hide(null, "sm")}
        ${hideSmall && media.hide("sm", "md")}
        ${hideMedium && media.hide("md", "lg")}
        ${hideLarge && media.hide("lg", "xl")}
        ${hideXLarge && media.hide("xl")}
    `}
    ${media.xxs.up`
        ${({ xxs, theme }: MediaWidthProps) =>
            xxs ? getWidthString(xxs, theme.gridCols) : `width: 100%`};

    `}
    ${media.xs.up`
        ${({ xs, theme }: MediaWidthProps) =>
            xs && getWidthString(xs, theme.gridCols)};

    `}
    ${media.sm.up`
        ${({ sm, theme }: MediaWidthProps) =>
            sm && getWidthString(sm, theme.gridCols)};

    `}
    ${media.md.up`
        ${({ md, theme }: MediaWidthProps) =>
            md && getWidthString(md, theme.gridCols)};
    `}
    ${media.lg.up`
        ${({ lg, theme }: MediaWidthProps) =>
            lg && getWidthString(lg, theme.gridCols)};
    `}
    ${media.xl.up`
        ${({ xl, theme }: MediaWidthProps) =>
            xl && getWidthString(xl, theme.gridCols)};
    `}
`;
Column.defaultProps = {
    maxWidth: "1280px",
    theme: {
        ...defaultTheme
    }
};

const Grid = styled.div<GridTypes>`
    ${layout}
    ${grid}
    ${color}
    ${space}
    display: grid;
    display: -ms-grid;
    ${props =>
        props.gridTemplateColumns &&
        css`
            grid-template-columns: repeat(
                ${props.gridTemplateColumns},
                minmax(0, 1fr)
            );
        `}
    ${props =>
        props.gridTemplateColumnsMobile &&
        css`
            ${media.sm.down`
                grid-template-columns: repeat(${props.gridTemplateColumnsMobile}, minmax(0, 1fr));
            `}
        `}
    ${props =>
        props.gridGap &&
        css`
            grid-gap: ${props.gridGap};
        `}
    ${props =>
        props.gridGapMobile &&
        css`
            ${media.sm.down`
                grid-gap: ${props.gridGapMobile};
            `}
        `}
`;
Grid.defaultProps = {
    gridGap: "1rem",
    gridTemplateColumns: 3,
    gridTemplateColumnsMobile: 1,
    theme: {
        ...defaultTheme
    }
};

export { Flexbox, Column, Grid };
